import { MenuItem } from '@affine/component/ui/menu';
import { AuthService } from '@affine/core/modules/cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { useI18n } from '@affine/i18n';
import { ArrowUpBigIcon, ImportIcon, PlusIcon } from '@blocksuite/icons/rc';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useLocalWorkspaceEnabled } from '../../../hooks/use-local-workspace-enabled';
import { useWorkspaceCreationQuota } from '../../../hooks/use-workspace-creation-quota';
import * as styles from './index.css';

export const AddWorkspace = ({
  onAddWorkspace,
  onNewWorkspace,
}: {
  onAddWorkspace?: () => void;
  onNewWorkspace?: () => void;
}) => {
  const t = useI18n();
  const navigate = useNavigate();
  const workspaceDialogService = useServiceOptional(WorkspaceDialogService);
  const session = useLiveData(useService(AuthService).session.session$);
  const isAuthenticated = session.status === 'authenticated';
  const enableLocalWorkspace = useLocalWorkspaceEnabled();

  const quota = useWorkspaceCreationQuota();
  const limitReached = !quota.loading && !quota.canCreate;

  const handleUpgrade = useCallback(() => {
    if (!isAuthenticated || !workspaceDialogService) {
      void navigate('/pricing');
      return;
    }
    workspaceDialogService.open('setting', { activeTab: 'workspace:plans' });
  }, [isAuthenticated, navigate, workspaceDialogService]);

  // Single-workspace plan with no upgrade path — hide entire footer
  const canImportLocalFile = BUILD_CONFIG.isElectron && enableLocalWorkspace;
  if (
    limitReached &&
    quota.limit === 1 &&
    !quota.upgradeAvailable &&
    !canImportLocalFile
  ) {
    return null;
  }

  return (
    <>
      {canImportLocalFile && (
        <MenuItem
          block={true}
          prefixIcon={<ImportIcon />}
          prefixIconClassName={styles.prefixIcon}
          onClick={onAddWorkspace}
          data-testid="add-workspace"
          className={styles.ItemContainer}
        >
          <div className={styles.ItemText}>
            {t['com.streamlain.workspace.local.import']()}
          </div>
        </MenuItem>
      )}
      {limitReached ? (
        <>
          <MenuItem
            block={true}
            prefixIcon={<PlusIcon />}
            prefixIconClassName={styles.prefixIcon}
            disabled
            data-testid="new-workspace"
            className={styles.ItemContainer}
          >
            <div className={styles.ItemText}>
              {t['com.streamlain.workspaceList.addWorkspace.limit-reached']({
                count: String(quota.ownedCount),
                limit: String(quota.limit),
              })}
            </div>
          </MenuItem>
          {quota.upgradeAvailable && (!isAuthenticated || quota.plan) && (
            <MenuItem
              block={true}
              prefixIcon={<ArrowUpBigIcon />}
              prefixIconClassName={styles.upgradeIcon}
              onClick={handleUpgrade}
              data-testid="upgrade-workspace-limit"
              className={styles.ItemContainer}
            >
              <div className={styles.upgradeText}>
                {!isAuthenticated || !quota.plan
                  ? t[
                      'com.streamlain.workspaceList.addWorkspace.sign-in-for-more'
                    ]()
                  : t['com.streamlain.workspaceList.addWorkspace.upgrade']({
                      plan: quota.plan,
                    })}
              </div>
            </MenuItem>
          )}
        </>
      ) : (
        <MenuItem
          block={true}
          prefixIcon={<PlusIcon />}
          prefixIconClassName={styles.prefixIcon}
          onClick={onNewWorkspace}
          data-testid="new-workspace"
          className={styles.ItemContainer}
        >
          <div className={styles.ItemText}>
            {enableLocalWorkspace
              ? t['com.streamlain.workspaceList.addWorkspace.create']()
              : t['com.streamlain.workspaceList.addWorkspace.create-cloud']()}
          </div>
        </MenuItem>
      )}
    </>
  );
};
